/*
Copyright (c) 2025 Mathilde Meyer, Naziha Beghdadi
Licensed under the MIT License (see LICENSE.txt file for details)
*/

import React from 'react';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import { Box, Typography } from '@mui/material';

function Preparation({ index, data, setData }) {
  const handleChange = (e) => {
    const { name, value } = e.target;
    const newData = [...data];
    newData[index] = {
      ...newData[index],
      [e.target.name]: e.target.value,
    };
    if (name === "preparation" && value === "no_preparation") {
      delete newData[index].preparationDays;
    }
    setData(newData);

  };

  return (
    <div>
      {/* Preparation Question */}
      <Box sx={{ mb: 1.5, mt: 2, p: 1.5, borderRadius: 1.5, backgroundColor: "#f7fbfd", border: "1px solid #dce8ef", fontSize: "0.84rem", color: "text.secondary", lineHeight: 1.65 }}>
        <Typography variant="body2">
          <strong>Special preparation</strong> is any treatment or condition applied to the animals <em>before</em> the
          first intervention — for example, putting them on a specific diet, restricting fluids, or pre-treating with a
          drug. The preparation period is added to the timeline and comes after acclimatation (if any).
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.75 }}>
          <strong>Special diet</strong> = a modified feed composition (e.g. high-fat, low-protein). &nbsp;
          <strong>Food restriction</strong> = reduced caloric intake or scheduled fasting. &nbsp;
          <strong>Tamoxifen treatment</strong> = used to activate Cre-lox genetic systems in inducible transgenic models.
        </Typography>
      </Box>
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel id="preparationLabel">
          Special preparation before first intervention?
        </InputLabel>
        <Select
          name="preparation"
          id="preparation"
          label="Special preparation before first intervention?"
          value={data[index].preparation || ''}
          onChange={handleChange}
        >
          <MenuItem value="no_preparation">No special preparation</MenuItem>
          <MenuItem value="special_diet">Special diet (modified feed composition)</MenuItem>
          <MenuItem value="food_restriction">Food restriction (caloric reduction / fasting)</MenuItem>
          <MenuItem value="water_restriction">Water restriction</MenuItem>
          <MenuItem value="antibiotics_treatment">Antibiotics treatment in water</MenuItem>
          <MenuItem value="tamoxifen_treatment">Tamoxifen treatment in food/gels (Cre-lox induction)</MenuItem>
          <MenuItem value="other_preparation">Other special preparation</MenuItem>
        </Select>
      </FormControl>

      {/* Show preparation days only when something is selected */}
      {data[index].preparation &&
        data[index].preparation !== 'no_preparation' && (
          <TextField
            id="preparationDays"
            name="preparationDays"
            label="Duration of special preparation (days)"
            variant="outlined"
            fullWidth
            sx={{ mb: 2 }}
            value={data[index].preparationDays || ''}
            onChange={handleChange}
            helperText="Number of days the preparation runs before the first intervention."
            onKeyDown={(e) => {
              const allowed = ["Backspace", "Tap", "ArrowLeft", "ArrowRight", "Delete"];
              if (!/[0-9]/.test(e.key) && !allowed.includes(e.key)) {
                e.preventDefault();
              }
            }}
            onPaste={(e) => {
              if (!/^\d+$/.test(e.clipboardData.getData("text"))) {
                e.preventDefault();
              }
            }}
          />
        )}

      <TextField
        id="cohorts"
        name="cohorts"
        label="Number of cohorts"
        variant="outlined"
        fullWidth
        sx={{ mb: 2 }}
        value={data[index].cohorts || ''}
        onChange={handleChange}
        helperText="A cohort is a group of animals that go through the experiment together at the same time. Enter 1 if all animals are run simultaneously."
      />

      <TextField
        id="repetitions"
        name="repetitions"
        label="Number of repetitions"
        variant="outlined"
        fullWidth
        sx={{ mb: 2 }}
        value={data[index].repetitions || ''}
        onChange={handleChange}
        helperText="Number of times the full protocol is run again on a fresh group of animals (e.g. to confirm reproducibility). Enter 1 if the experiment is run once. This is different from cohorts, which run in parallel."
      />
    </div>
  );
}

export default Preparation;
